import { FC, useMemo, useState } from "react";
import { Button, Dialog, Flex, Text } from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";

import { CASE_FIELD_SOURCE_MODE, CASE_FILE_KIND, CaseFieldSourceModel, CaseModel } from "@shared/models";
import { FONT_WEIGHT } from "@assets/theme/foundations/textStyle";
import { selectors } from "@containers/Cases/store";
import { removeCaseDocument } from "@containers/Cases/store/actions";
import {
  CASE_DOCUMENT_INFO,
  MEDICAL_FIELD_LABELS,
  MEDICAL_FIELDS_FORM_ORDER,
  UploadedCaseFileKind,
} from "@containers/Cases/constants";
import { hasMedicalFieldText, pluralize } from "@containers/Cases/utils/caseDocuments";

import { CaseDocumentModalHeader } from "../CaseDocumentModal";
import RemoveCaseDocumentOption from "./RemoveCaseDocumentOption";

const MEDICAL_TEXT_MODES = [CASE_FIELD_SOURCE_MODE.MEDICAL, CASE_FIELD_SOURCE_MODE.BOTH];

const getMedicalTextSources = (caseObj: CaseModel | null) => {
  const sources = MEDICAL_FIELDS_FORM_ORDER.flatMap((field) => {
    const source = caseObj?.field_sources?.find((item) => item.field === field);

    return source?.mode && MEDICAL_TEXT_MODES.includes(source.mode) && hasMedicalFieldText(caseObj, field)
      ? [source]
      : [];
  });

  return {
    uneditedSources: sources.filter(({ is_edited }) => !is_edited),
    editedSources: sources.filter(({ is_edited }) => is_edited),
  };
};

const getFieldNames = (sources: CaseFieldSourceModel[]) =>
  sources.map(({ field }) => MEDICAL_FIELD_LABELS[field]).join(", ");

interface RemoveCaseDocumentModalProps {
  caseId: number;
  kind: UploadedCaseFileKind;
  onClose: () => void;
}

const RemoveCaseDocumentModal: FC<RemoveCaseDocumentModalProps> = ({ caseId, kind, onClose }) => {
  const dispatch = useDispatch();
  const caseObj = useSelector(selectors.getCase());
  const caseDocuments = useSelector(selectors.getCaseDocuments());
  const [clearText, setClearText] = useState(kind === CASE_FILE_KIND.MEDICAL);
  const [clearEdited, setClearEdited] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isMedical = kind === CASE_FILE_KIND.MEDICAL;
  const files = caseDocuments.find((caseDocument) => caseDocument.kind === kind)?.files ?? [];
  const { uneditedSources, editedSources } = useMemo(
    () => (isMedical ? getMedicalTextSources(caseObj) : { uneditedSources: [], editedSources: [] }),
    [caseObj, isMedical],
  );
  const hasTextToClear = isMedical ? uneditedSources.length > 0 : !!caseObj?.case_notes;

  const handleRemove = () => {
    setIsRemoving(true);
    setError(null);
    dispatch(
      removeCaseDocument({
        caseId,
        kind,
        clearText: hasTextToClear && clearText,
        clearEditedFields: clearEdited ? editedSources.map(({ field }) => field) : [],
        cb: onClose,
        onError: (message) => {
          setIsRemoving(false);
          setError(message);
        },
      }),
    );
  };

  return (
    <>
      <CaseDocumentModalHeader
        title={`Remove ${CASE_DOCUMENT_INFO[kind].name.toLowerCase()}?`}
        subtitle={`${files.length === 1 ? files[0].name : pluralize(files.length, "file")} will be deleted from this case. This can't be undone.`}
      />
      <Dialog.Body display="flex" flexDirection="column" gap={3}>
        {hasTextToClear && (
          <RemoveCaseDocumentOption
            title="Clear text added from this document"
            note={
              isMedical
                ? `${getFieldNames(uneditedSources)}. PSR text stays.`
                : "Clears everything in Case Notes, including your edits."
            }
            checked={clearText}
            disabled={isRemoving}
            onChange={setClearText}
          />
        )}
        {editedSources.length > 0 && (
          <Flex
            direction="column"
            gap="10px"
            px={4}
            py="14px"
            borderRadius="8px"
            border="1px solid"
            borderColor="score.midText/30"
            bg="score.midBg"
          >
            <Text fontSize="13px" lineHeight={1.5} color="brand.warningText">
              <Text as="span" fontWeight={FONT_WEIGHT.BOLD}>
                Edited after upload, kept by default:
              </Text>{" "}
              {getFieldNames(editedSources)}
            </Text>
            <RemoveCaseDocumentOption
              title="Also clear edited fields"
              checked={clearEdited}
              disabled={isRemoving}
              onChange={setClearEdited}
              p={0}
              bg="transparent"
            />
          </Flex>
        )}
        {error && (
          <Text fontSize="13px" color="brand.error">
            {error}
          </Text>
        )}
      </Dialog.Body>
      <Dialog.Footer gap={3} mt={2}>
        <Button variant="neutral" fontSize="14px" disabled={isRemoving} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          fontSize="14px"
          bg="brand.error"
          _hover={{ bg: "brand.error" }}
          loading={isRemoving}
          onClick={handleRemove}
        >
          Remove
        </Button>
      </Dialog.Footer>
    </>
  );
};

export default RemoveCaseDocumentModal;
